export type CustomerRecordType =
  | 'Customer'
  | 'Site'
  | 'GroupingNode'
  | 'Aggregate';

export type CustomerStatus =
  | 'prospect'
  | 'onboarding'
  | 'active'
  | 'onHold'
  | 'suspended'
  | 'archived';

export type CustomerBucket = 'group' | 'independent' | 'unassigned';

export interface CustomerReadiness {
  ready: boolean;
  gaps: number;
}

export interface CustomerPartnerFunctions {
  payerRef: string;
  billToRef: string;
}

export interface CustomerHierarchy {
  bucket: CustomerBucket;
  groupName: string | null;
  groupAvatarCode: string | null;
  subNodeName: string | null;
  parentName: string | null;
  isShipToSite: boolean;
  isDemandNode: boolean;
  banner: string | null;
}

export interface Customer {
  id: string;
  customerId: string;
  name: string;
  type: string;
  contactName: string;
  contactEmail: string;
  contactNumber: string;
  hqId: string;
  hqName: string;
  address: string;
  postcode: string;
  country: string;
  operatingHoursFrom: string;
  operatingHoursTo: string;
  notes: string;
  recordType: CustomerRecordType;
  typeLabel: string;
  market: string;
  channel: string | null;
  hierarchy: CustomerHierarchy;
  partners: CustomerPartnerFunctions;
  readiness: CustomerReadiness | null;
  status: CustomerStatus;
  isArchived: boolean;
}

export const partnersDiverge = (partners: CustomerPartnerFunctions): boolean =>
  partners.payerRef !== partners.billToRef;

export const accountGroupKeyOf = (customer: Customer): string => {
  if (customer.hierarchy.bucket === 'group' && customer.hierarchy.groupName) {
    return customer.hierarchy.groupName;
  }
  if (customer.hierarchy.bucket === 'independent') return 'Independents';
  return 'Unassigned';
};

export const hierarchyPathOf = (customer: Customer): string[] => {
  const { hierarchy } = customer;
  if (hierarchy.bucket === 'group') {
    return [hierarchy.groupName, hierarchy.subNodeName].filter(
      (part): part is string => !!part
    );
  }
  if (hierarchy.bucket === 'independent') return ['Independent'];
  return [];
};

export const statusBadgeIntentOf = (
  status: CustomerStatus
): 'info' | 'warning' | 'neutral' | 'danger' => {
  switch (status) {
    case 'active':
      return 'info';
    case 'onboarding':
    case 'prospect':
      return 'warning';
    case 'suspended':
      return 'danger';
    default:
      return 'neutral';
  }
};

export const statusLabelOf = (status: CustomerStatus): string => {
  switch (status) {
    case 'active':
      return 'Active';
    case 'onboarding':
      return 'Onboarding';
    case 'onHold':
      return 'On hold';
    case 'prospect':
      return 'Prospect';
    case 'suspended':
      return 'Suspended';
    case 'archived':
      return 'Archived';
    default:
      return status;
  }
};
